import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { Routes } from '../../../../routes';
import { Link } from 'react-router-dom';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colors } from 'styles/settings';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faBan, faCog, faPen } from '@fortawesome/pro-regular-svg-icons';
import {
    BankAccount,
    CreditCard,
    CustomLocalProduct,
    PayCoordinator,
    Reload,
} from 'components/custom-icons';
import { BarCode } from 'components/custom-icons';
import { PhysicalCard } from 'components/custom-icons';
import OrderingSettingsTable, { tableDataType } from './OrderingSettingsTable';
import { Modal, ModalSize, ModalFooter, ModalSecondaryBtn } from 'components/modal/Modal';
import { Btn } from 'components/btn/Btn';
import {
    selectIsPayCoordinatorEnabled,
    selectIsShipToCoordinatorEnabled,
    getOptionsThunk,
} from 'common/features/store/duck/organization/duck';
import { useRootSelector } from 'common/features/featuresReducer';
import { setOptions } from 'common/api/e-comm/options';
import useFeatureFlags, { FeatureFlagTypes } from 'common/hooks/useFeatureFlags';
import { useDispatch } from 'react-redux';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useP2PInviteSettings, useP2PConfirmModal } from './p2p-invite-settings/P2PInviteSettings';
import { ToggleTable } from './ToggleTable';
import { PaymentType } from 'common/api/e-comm/payment-types';
import { usePaymentOptions } from 'common/hooks/usePaymentOptions';
import { VacationModeBanner } from './vacation-mode/Banners';
import { useVacationModeState } from './vacation-mode/utils';
import { useModalControls } from 'hooks/useModalControls';
import { VacationModeModal } from './vacation-mode/VacationModeModal';

const availableIcon = (
    <FontAwesomeIcon icon={faCheckCircle} color={colors.success} className="mr-2" size="1x" />
);

const limitedAvailabilityIcon = (
    <FontAwesomeIcon
        icon={faExclamationTriangle}
        color={colors.warningDark}
        className="mr-2"
        size="1x"
    />
);

const notAvailableIcon = (
    <FontAwesomeIcon icon={faBan} color={colors.error} className="mr-2.5" size="1x" />
);

const SHIPMENT_DATA: tableDataType[] = [
    {
        description: {
            icon: <BarCode width={20} height={20} />,
            text: 'eGift Cards',
        },
        payCoordinatorOn: {
            icon: availableIcon,
            text: 'All items available',
        },
        payCoordinatorOff: {
            icon: availableIcon,
            text: 'All items available',
        },
    },
    {
        description: {
            icon: <Reload width={20} height={20} />,
            text: 'Reloadable (Adding Money)',
        },
        payCoordinatorOn: {
            icon: availableIcon,
            text: 'All items available',
        },
        payCoordinatorOff: {
            icon: availableIcon,
            text: 'All items available',
        },
    },
    {
        description: {
            icon: <Reload width={20} height={20} />,
            text: 'Reloadable (New Cards)',
        },
        payCoordinatorOn: {
            icon: availableIcon,
            text: 'All items available',
        },
        payCoordinatorOff: {
            icon: limitedAvailabilityIcon,
            text: 'Limited availability',
        },
    },
    {
        description: {
            icon: <PhysicalCard width={20} height={20} />,
            text: 'Physical Cards',
        },
        payCoordinatorOn: {
            icon: availableIcon,
            text: 'All items available',
        },
        payCoordinatorOff: {
            icon: limitedAvailabilityIcon,
            text: 'Limited availability',
        },
    },
    {
        description: {
            icon: <CustomLocalProduct width={20} height={20} />,
            text: 'Custom Local Products',
        },
        payCoordinatorOn: {
            icon: availableIcon,
            text: 'All items available',
        },
        payCoordinatorOff: {
            icon: notAvailableIcon,
            text: 'Not available',
        },
    },
];

const getPaymentsData = ({ debitEnabled }: { debitEnabled: boolean }): tableDataType[] => [
    {
        description: {
            icon: <BankAccount width={20} height={20} />,
            text: 'Bank Account',
        },
        payCoordinatorOn: {
            icon: availableIcon,
            text: 'Available',
        },
        payCoordinatorOff: {
            icon: availableIcon,
            text: 'Available',
        },
    },
    {
        description: {
            icon: <CreditCard width={20} height={20} />,
            text: debitEnabled ? 'Credit and Debit Cards' : 'Credit Cards',
        },
        payCoordinatorOn: {
            icon: availableIcon,
            text: 'Available',
        },
        payCoordinatorOff: {
            icon: availableIcon,
            text: 'Available',
        },
    },
    {
        description: {
            icon: <PayCoordinator width={20} height={20} className="sm:w-auto w-fit" />,
            text: 'Pay Coordinator',
        },
        payCoordinatorOn: {
            icon: availableIcon,
            text: 'Available',
        },
        payCoordinatorOff: {
            icon: notAvailableIcon,
            text: 'Not available',
        },
    },
];

export const OrganizationSettings = ({
    selectedOrganizationId,
    isNewDashboard = false,
}: {
    selectedOrganizationId: string;
    isNewDashboard?: boolean;
}) => {
    useScrollToTop();
    const dispatch = useDispatch<any>();
    const isPayCoordinatorEnabled = useRootSelector(selectIsPayCoordinatorEnabled);
    const isShipCoordinatorEnabled = useRootSelector(selectIsShipToCoordinatorEnabled);
    const { loadPaymentOptionsData } = usePaymentOptions();
    const paymentTypes = useRootSelector((s) => s.store.organization.paymentTypes);
    const hasOnlinePaymentType = Boolean(
        paymentTypes?.includes(PaymentType.BankAccount) ||
            paymentTypes?.includes(PaymentType.CreditCard)
    );
    const { isEnabled, loadingFlags } = useFeatureFlags();

    const [isPayCoordinatorOn, setIsPayCoordinatorOn] = React.useState(false);
    const [isShipToCoordinatorOn, setIsShipToCoordinatorOn] = React.useState(false);

    const [showWarningModal, setShowWarningModal] = React.useState(false);

    useLayoutEffect(() => {
        loadPaymentOptionsData();
    }, []);
    // vacation mode related
    const { state, isVacationModeScheduled } = useVacationModeState();
    const vacationModeModalCtrls = useModalControls();

    useEffect(() => {
        setIsShipToCoordinatorOn(isShipCoordinatorEnabled ?? false);
        setIsPayCoordinatorOn(!!isPayCoordinatorEnabled ?? false);
    }, [isShipCoordinatorEnabled, isPayCoordinatorEnabled]);

    const onShipToCoordinatorToggle = (newValue: boolean) => {
        if (!newValue) {
            setShowWarningModal(true);
            return;
        }

        confirmShipToCoordinatorToggle(newValue);
    };

    const confirmPayToCoordinatorToggle = async (newValue: boolean) => {
        try {
            await setOptions(
                {
                    isShipToCoordinatorEnabled: !!isShipCoordinatorEnabled,
                    isPayCoordinatorEnabled: newValue,
                },
                selectedOrganizationId
            );
            dispatch(getOptionsThunk());
        } catch (e) {
            setIsPayCoordinatorOn(!newValue);
        }

        setIsPayCoordinatorOn(newValue);
    };

    const confirmShipToCoordinatorToggle = async (newValue: boolean) => {
        try {
            await setOptions(
                {
                    isPayCoordinatorEnabled: !!isPayCoordinatorEnabled,
                    isShipToCoordinatorEnabled: newValue,
                },
                selectedOrganizationId
            );
            dispatch(getOptionsThunk());
        } catch (e) {
            setIsShipToCoordinatorOn(!newValue);
        }

        setIsShipToCoordinatorOn(newValue);
    };

    const { isP2PInviteEnabled, updateP2PInviteSettings, isUpdatingP2PInviteSettings } =
        useP2PInviteSettings();

    const confirmP2PInviteEnabled = () => {
        setShowConfirmationWarning(true);
    };

    const { setShowConfirmationWarning, viewP2PConfirmationWarning } = useP2PConfirmModal({
        isP2PInviteEnabled,
        onConfirm: () => {
            updateP2PInviteSettings(!isP2PInviteEnabled);
            setShowConfirmationWarning(false);
        },
    });

    const paymentsLink = isEnabled(FeatureFlagTypes.enable_unified_coordinator_dashboard) ? (
        <Link to={Routes.CoordinatorDashboardPaymentTypes}>Online Payments.</Link>
    ) : (
        <a target="_blank" href="/c/Org/Manage/PrestoPay">
            Online Payments.
        </a>
    );

    const toggleParticipantShipToCoordinator = () => {
        if (!state?.isVacationModeActive) {
            onShipToCoordinatorToggle(!isShipToCoordinatorOn);
        }
    };

    const toggleParticipantPaymentsToCoordinator = () => {
        if (!state?.isVacationModeActive) {
            confirmPayToCoordinatorToggle(!isPayCoordinatorOn);
        }
    };

    const isVacationModeFeatureEnabled = isEnabled(
        FeatureFlagTypes.enable_coordinator_vacation_mode
    );

    return (
        <div className="max-w-full sm:flex-1 flex sm:pl-8">
            <div className="md:ml-1 ml-auto mr-auto sm:max-w-3xl max-w-full mt-4 px-1 md:px-0 text-left">
                {!isNewDashboard ? (
                    <Link
                        className="text-brand text-sm font-semibold"
                        to={Routes.ShopAccountSettingsCoordinatorTab}
                    >
                        <FontAwesomeIcon
                            icon={faChevronLeft}
                            color={colors.bonusPink}
                            className="mr-2"
                            style={{ padding: '0.5px' }}
                            size="1x"
                        />
                        Back to Main Dashboard
                    </Link>
                ) : null}
                <h1 className="text-2xl text-brand-dark font-semibold mt-10 mb-4">
                    Organization Settings
                </h1>
                {isEnabled(FeatureFlagTypes.coordinator_toggles) ? (
                    <>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <h3 className="text-l">Ordering Settings</h3>
                            {hasOnlinePaymentType && isVacationModeFeatureEnabled ? (
                                <Btn
                                    onClick={vacationModeModalCtrls.toggleModalVisibility}
                                    isSecondary={
                                        !!(state?.isVacationModeActive || isVacationModeScheduled)
                                    }
                                    className="o:text-sm py-2.5 mt-5 mb-2.5 sm:m-0"
                                >
                                    <FontAwesomeIcon
                                        className="mr-2"
                                        icon={
                                            state?.isVacationModeActive || isVacationModeScheduled
                                                ? faPen
                                                : faCog
                                        }
                                    />
                                    {state?.isVacationModeActive || isVacationModeScheduled
                                        ? 'Edit Vacation Mode'
                                        : 'Vacation Mode'}
                                </Btn>
                            ) : null}
                        </div>
                        <OrderingSettingsTable
                            title="Participant Shipments to Coordinator"
                            subTitle={
                                <div>
                                    <p className="font-normal">
                                        This controls the 'Ship to Coordinator' delivery option.
                                        Turning this <strong className="font-bold">OFF</strong>{' '}
                                        limits the products available to participants and
                                        potentially your organizations’ earnings.
                                    </p>
                                    <VacationModeBanner
                                        originalToggleValue={
                                            state?.originalIsShipToCoordinatorEnabled
                                        }
                                        startDate={state?.vacationModeStartDate || null}
                                        endDate={state?.vacationModeEndDate || null}
                                        editVacationMode={
                                            vacationModeModalCtrls.toggleModalVisibility
                                        }
                                        isVacationModeActive={
                                            !!state?.isVacationModeActive &&
                                            isVacationModeFeatureEnabled
                                        }
                                        isVacationModeScheduled={
                                            !!isVacationModeScheduled &&
                                            isVacationModeFeatureEnabled
                                        }
                                    />
                                </div>
                            }
                            variant="Ship to"
                            isToggleOn={!!isShipToCoordinatorOn}
                            setIsToggleOn={toggleParticipantShipToCoordinator}
                            data={SHIPMENT_DATA}
                            learnMoreVariant="ship-to-coordinator-faqs"
                        />
                        {!hasOnlinePaymentType && !!isPayCoordinatorOn ? (
                            <p className="mt-7 text-sm text-error">
                                <FontAwesomeIcon
                                    color={colors.error}
                                    icon={faExclamationTriangle}
                                    size="1x"
                                    className="mr-2"
                                />
                                This option is unavailable because your organization is not enrolled
                                in {paymentsLink}
                            </p>
                        ) : null}
                        <OrderingSettingsTable
                            title="Participant Payments to Coordinator"
                            variant="Pay"
                            isToggleOn={!!isPayCoordinatorOn}
                            setIsToggleOn={toggleParticipantPaymentsToCoordinator}
                            data={getPaymentsData({
                                debitEnabled: isEnabled(FeatureFlagTypes.enable_debit_web),
                            })}
                            isDisabled={!hasOnlinePaymentType && !!isPayCoordinatorOn}
                            learnMoreVariant="pay-coordinator-faqs"
                            subTitle={
                                hasOnlinePaymentType && isVacationModeFeatureEnabled ? (
                                    <VacationModeBanner
                                        originalToggleValue={state?.originalIsPayCoordinatorEnabled}
                                        startDate={state?.vacationModeStartDate || null}
                                        endDate={state?.vacationModeEndDate || null}
                                        editVacationMode={
                                            vacationModeModalCtrls.toggleModalVisibility
                                        }
                                        isVacationModeActive={!!state?.isVacationModeActive}
                                        isVacationModeScheduled={!!isVacationModeScheduled}
                                    />
                                ) : undefined
                            }
                        />
                        <Modal
                            title=""
                            isOpen={showWarningModal}
                            onClose={() => setShowWarningModal(false)}
                            showCloseButton={true}
                            size={[ModalSize.LARGE, ModalSize.LARGE]}
                        >
                            <div className="flex flex-col items-center">
                                <FontAwesomeIcon
                                    color={colors.warningDark}
                                    icon={faExclamationTriangle}
                                    size="2x"
                                />
                                <div className="mt-6 text-center w-auto sm:w-10/12 ">
                                    <p className="text-3xl font-semibold mb-4">
                                        Disable Ship to Coordinator?
                                    </p>
                                    <p className="text-grey-2 font-normal text-md pb-5">
                                        Disabling this option will limit your participants'
                                        available online products.
                                    </p>
                                    <p className="text-grey-2 font-normal text-md">
                                        Custom local products will only be available through bulk
                                        and proxy orders.
                                    </p>
                                </div>
                            </div>
                            <div className="sm:-mt-3 mb-3">
                                <ModalFooter>
                                    <Btn
                                        onClick={() => {
                                            window.open(
                                                'https://www.raiseright.com/m/organization-settings-faqs',
                                                '_blank'
                                            );
                                        }}
                                        type="button"
                                        className="px-2"
                                    >
                                        Learn More
                                    </Btn>
                                    <ModalSecondaryBtn
                                        onClick={() => {
                                            confirmShipToCoordinatorToggle(!isShipToCoordinatorOn);
                                            setShowWarningModal(false);
                                        }}
                                    >
                                        Yes, Please Disable
                                    </ModalSecondaryBtn>
                                </ModalFooter>
                            </div>
                        </Modal>
                    </>
                ) : null}
                {isEnabled(FeatureFlagTypes.enable_invite_friends_family) ? (
                    <>
                        <h3 className="text-l mt-6">Invite Settings</h3>
                        <ToggleTable
                            title="Invite Friends & Family"
                            isToggleOn={!!isP2PInviteEnabled}
                            setIsToggleOn={() => confirmP2PInviteEnabled()}
                            isDisabled={
                                isP2PInviteEnabled === undefined || isUpdatingP2PInviteSettings
                            }
                            learnMoreLink="https://www.raiseright.com/m/invite-settings"
                        >
                            <div className="max-w-xl pr-2">
                                <p>
                                    Turn this <b>Off</b> to prevent participants from inviting other
                                    people to your program.
                                    <br />
                                    You can still use the Coordinator Invite Tools to invite people
                                    to your program.
                                </p>
                                <br />
                                <p>
                                    Turn this <b>On</b> to allow participants to directly invite
                                    others to join your organization.
                                    <br />
                                </p>
                                <br />
                                <p>
                                    <b>Note:</b> Please ensure that invite links are only shared
                                    directly with people associated with your organization. Sharing
                                    these resources on public social media pages or websites can
                                    result in unwanted or fraudulent enrollments.
                                </p>
                            </div>
                        </ToggleTable>
                        {viewP2PConfirmationWarning}
                    </>
                ) : null}
                <VacationModeModal
                    modalControls={vacationModeModalCtrls}
                    vacationModeState={state}
                    isVacationModeScheduled={!!isVacationModeScheduled}
                />
            </div>
        </div>
    );
};
